// src/features/auth/authThunks.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { LoginRequest, LoginResponse, User } from "../../types";
import apiService from "../api";
import {
  setAuthToken,
  setUserData,
  clearAuthCookies,
  getUserData,
  getAuthToken,
  setHubId,
  setHubName,
  setRoleHubRelations,
  isAuthenticated as isAuthed,
} from "../../utils/cookieUtils";
import type { RoleHubRelation } from "../../utils/cookieUtils";

// Separate function to call GetUserByUserId API
const fetchUserDetailsByUserId = async (userid: number): Promise<{ success: boolean; hubid?: number; hubname?: string; user?: any; error?: string }> => {
  try {
    console.log(`[GetUserByUserId] Starting API call for userid: ${userid}`);
    console.log(`[GetUserByUserId] Using API service method`);
    
    // Check if token is available before making the call
    const currentToken = getAuthToken();
    console.log(`[GetUserByUserId] Current token available:`, currentToken ? 'Yes' : 'No');
    console.log(`[GetUserByUserId] Token preview:`, currentToken ? currentToken.substring(0, 20) + '...' : 'None');
    
    const responseData: any = await apiService.getUserByUserId(userid);
    console.log(`[GetUserByUserId] Response:`, responseData);
    
    // Handle different response structures
    let userData = null;
    let hubid = null;
    let hubname = null;
    
    if (responseData?.success && responseData?.data) {
      userData = responseData.data;
      hubid = responseData.data.hubid;
      hubname = responseData.data.hubname;
    } else if (responseData?.data) {
      userData = responseData.data;
      hubid = responseData.data.hubid;
      hubname = responseData.data.hubname;
    } else if (responseData?.result) {
      userData = responseData.result;
      hubid = responseData.result.hubid;
      hubname = responseData.result.hubname;
    } else if (responseData) {
      userData = responseData;
      hubid = responseData.hubid;
      hubname = responseData.hubname;
    }
    
    if (hubid !== null && hubid !== undefined) {
      console.log(`[GetUserByUserId] ✅ Successfully fetched hubid: ${hubid}, hubname: ${hubname} for user: ${userid}`);
      return {
        success: true,
        hubid: Number(hubid),
        hubname: hubname || '',
        user: userData
      };
    } else {
      console.warn(`[GetUserByUserId] ⚠️ No hubid found in response for userid: ${userid}`);
      return {
        success: false,
        error: 'Hub ID not found in user details'
      };
    }
    
  } catch (error: any) {
    console.error('[GetUserByUserId] ❌ Error fetching user details by userid:', error);
    
    let errorMessage = 'Failed to fetch user details';
    
    if (error.response) {
      errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
    } else {
      errorMessage = error.message || 'Unknown error occurred';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

// Fetch role-hub relations for the user (single or multiple hubs per role)
const fetchRoleHubRelationListByUserId = async (userid: number): Promise<{ success: boolean; data?: RoleHubRelation[]; error?: string }> => {
  try {
    const response: any = await apiService.getRoleHubRelationListByUserId(userid);
    const ok = response?.isSuccess ?? response?.success ?? (response?.data != null);
    const data = response?.data ?? response?.result ?? (Array.isArray(response) ? response : null);
    const list = Array.isArray(data) ? (data as RoleHubRelation[]) : [];
    return { success: !!ok, data: list };
  } catch (error: any) {
    console.error('[GetRoleHubRelationListByUserId] Error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch role-hub relations',
    };
  }
};

export const logoutThunk = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>(
  "auth/logout",
  async (userId: string, { rejectWithValue }: { rejectWithValue: (value: string) => any }) => {
    try {
      await apiService.logout(userId);
      clearAuthCookies();
    } catch (error: any) {
      // Even if logout API fails, we should still clear local data
      clearAuthCookies();
      if (error.response) {
        const message = error.response.data?.message || error.response.data?.error || "Logout failed";
        return rejectWithValue(message);
      } else if (error.request) {
        return rejectWithValue("Network error during logout");
      } else {
        return rejectWithValue(error.message || "Logout failed");
      }
    }
  }
);

export const loginThunk = createAsyncThunk<
  LoginResponse,     
  LoginRequest,        
  { rejectValue: string }
>(
  "auth/login",
  async (credentials: LoginRequest, { rejectWithValue, dispatch }: { rejectWithValue: (value: string) => any; dispatch: any }) => {
    try {
      const response = await apiService.login(credentials);
      
      let loginData: LoginResponse;
      if (response && response.success && response.data) {
        loginData = response.data;
      } else if (response && response.data) {
        loginData = response.data;
      } else if (response && (response as any).result && (response as any).token) {
        loginData = response as unknown as LoginResponse;
      } else {
        return rejectWithValue((response as any)?.error || (response as any)?.message || "Login failed");
      }

      // Check for staff status and role
      if (loginData.result.staffstatus === 2) {
        return rejectWithValue("Your account is inactive. Please contact Super Admin.");
      }

      console.log('[LoginThunk] Login successful, user data:', loginData.result);
      console.log('[LoginThunk] Login data:', loginData.token);
      console.log('[LoginThunk] User roleid:', loginData.result.roleid);
      
      // Set token and user data in cookies first
      setAuthToken(loginData.token);
      setUserData(loginData.result);
      
      // Call GetUserByUserId API for all users (including Super Admin)
      console.log(`[LoginThunk] Calling GetUserByUserId API for userid: ${loginData.result.userid}, roleid: ${loginData.result.roleid}`);
      console.log(`[LoginThunk] Token set in cookies:`, loginData.token ? 'Yes' : 'No');
      
      // Add a small delay to ensure token is properly set in interceptor
      await new Promise(resolve => setTimeout(resolve, 200));

      // 1) Call GetRoleHubRelationListByUserIdAsync to get user's role-hub relations (single or multiple hubs)
      const roleHubResult = await fetchRoleHubRelationListByUserId(loginData.result.userid);
      const relations: RoleHubRelation[] = roleHubResult.success && roleHubResult.data ? roleHubResult.data : [];

      if (relations.length > 0) {
        setRoleHubRelations(relations);
        const firstHubId = relations[0].hubid;

        if (relations.length === 1) {
          // Single hub: set current hub; try GetUserByUserId for hubname, else resolve by hub id
          setHubId(firstHubId);
          try {
            const userDetailsResult = await fetchUserDetailsByUserId(loginData.result.userid);
            if (userDetailsResult.success && (userDetailsResult.hubid === firstHubId || userDetailsResult.hubname)) {
              if (userDetailsResult.hubname) setHubName(userDetailsResult.hubname);
            }
          } catch {
            // optional: could call getHubById(firstHubId) for name
          }
        } else {
          // Multiple hubs: set first hub as selected; hub name optional (can be shown in switcher)
          setHubId(firstHubId);
          try {
            const userDetailsResult = await fetchUserDetailsByUserId(loginData.result.userid);
            if (userDetailsResult.success && userDetailsResult.hubid === firstHubId && userDetailsResult.hubname) {
              setHubName(userDetailsResult.hubname);
            }
          } catch {
            // optional
          }
        }
      } else {
        // No relations (e.g. Super Admin): keep existing flow with GetUserByUserId only
        try {
          const userDetailsResult = await fetchUserDetailsByUserId(loginData.result.userid);
          if (userDetailsResult.success && userDetailsResult.hubid) {
            setHubId(userDetailsResult.hubid);
            if (userDetailsResult.hubname) setHubName(userDetailsResult.hubname);
          }
        } catch (userDetailsError) {
          console.error('[LoginThunk] Error fetching user details after login:', userDetailsError);
        }
      }

      return loginData;
    } catch (error: any) {
      
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error || getErrorMessage(status);
        return rejectWithValue(message);
      } else if (error.request) {
        return rejectWithValue("Network error. Please check your connection.");
      } else {
        return rejectWithValue(error.message || "Login failed. Please try again.");
      }
    }
  }
);

const getErrorMessage = (status: number): string => {
  switch (status) {
    case 400:
      return "Invalid credentials. Please check your username and password.";
    case 401:
      return "Invalid username or password.";
    case 403:
      return "Access denied. Please contact administrator.";
    case 404:
      return "Login service not found.";
    case 500:
      return "Server error. Please try again later.";
    default:
      return "Login failed. Please try again.";
  }
};

const authService = {
  isAuthenticated(): boolean {
    return isAuthed();
  },

  getCurrentUser(): any | null {
    return getUserData();
  },

  async login(credentials: LoginRequest): Promise<{ success: boolean; message: string; user?: any }> {
    try {
      const res = await apiService.login(credentials);
      if (res.success && res.data) {
        setAuthToken(res.data.token);
        setUserData(res.data.result);
        return { success: true, message: "Login successful", user: res.data.result };
      }
      return { success: false, message: res.error || "Login failed" };
    } catch (err: any) {
      const message = err?.message || "Login failed";
      return { success: false, message };
    }
  },

  async getProfile(): Promise<{ success: boolean; user?: any; message?: string }> {
    try {
      const res = await apiService.getProfile() as any;
      if (res.success) {
        const user = res.data?.user ?? res.data ?? null;
        if (user) setUserData(user);
        return { success: true, user };
      }
      return { success: false, message: res.error || "Failed to fetch profile" };
    } catch (err: any) {
      return { success: false, message: err?.message || "Failed to fetch profile" };
    }
  },

  clearUserData(): void {
    clearAuthCookies();
  },

  updateUserData(partial: any): void {
    const existing = getUserData() || {};
    setUserData({ ...existing, ...partial });
  },
};

export default authService;
